import { api } from './axios'
import type {
  CreateQSEEnterpriseTenantDto,
  CreateTenantDto,
  CreateTenantPublicDto,
  GetUserTenantsResponse,
  PermanentDeleteTenantParams,
  PermanentDeleteTenantResponseDto,
  TenantDto,
  TenantFilters,
  TenantManagementDto,
  UpdateTenantDto,
  UpdateTenantPlanDto,
} from './types'

/**
 * Get User Tenants
 * This api gets the tenants the user has access to
 */
export const getUserTenants = async (
  resellerId?: string,
  filters?: TenantFilters,
): Promise<GetUserTenantsResponse> => {
  const params = new URLSearchParams()

  if (resellerId) params.append('resellerId', resellerId)
  if (filters?.search) params.append('search', filters.search)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.limit) params.append('limit', filters.limit.toString())
  if (filters?.offset) params.append('offset', filters.offset.toString())
  if (filters?.sortBy) params.append('sortBy', filters.sortBy)
  if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder)
  // Note: _sortTimestamp is not sent to server - it's only for query key differentiation

  const queryString = params.toString()
  const url = queryString ? `/tenants?${queryString}` : '/tenants'

  const response = await api.get<GetUserTenantsResponse>(url)

  return response.data
}

/**
 * Create a new tenant under a reseller
 */
export const createTenant = async (tenantData: CreateTenantDto): Promise<TenantDto> => {
  const response = await api.post<TenantDto>('/tenants', tenantData)

  return response.data
}

/**
 * Get all tenants for a specific reseller
 */
export const getTenantsByReseller = async (resellerId: string): Promise<TenantDto[]> => {
  const response = await api.get<TenantDto[]>('/tenants', { params: { resellerId } })

  return response.data
}

/**
 * Get a specific tenant by its ID with comprehensive details
 */
export const getTenantById = async (id: string): Promise<TenantManagementDto> => {
  const response = await api.get<TenantManagementDto>(`/tenants/${id}`)

  return response.data
}

/**
 * Public endpoint for tenant self-registration
 */
export const createTenantPublic = async (tenantData: CreateTenantPublicDto): Promise<TenantDto> => {
  const response = await api.post<TenantDto>('/public/tenant', tenantData, {
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })

  return response.data
}

/**
 * Create a new QSE Enterprise tenant organization
 * Establishes a new tenant account with admin user creation, Cognito integration, and infrastructure provisioning
 */
export const createQSEEnterpriseTenant = async (
  tenantData: CreateQSEEnterpriseTenantDto,
): Promise<TenantManagementDto> => {
  const response = await api.post<TenantManagementDto>('/tenants', tenantData)

  return response.data
}

/**
 * Update the subscription plan for a specific tenant organization
 */
export const updateTenantPlan = async (
  id: string,
  planData: UpdateTenantPlanDto,
): Promise<TenantManagementDto> => {
  const response = await api.patch<TenantManagementDto>(`/tenants/${id}/plan`, planData)

  return response.data
}

/**
 * Update tenant organization details including plan assignment and company name
 * Supports partial updates - either planId, name, or both can be updated in a single request
 */
export const updateTenant = async (
  id: string,
  tenantData: UpdateTenantDto,
): Promise<TenantManagementDto> => {
  const response = await api.patch<TenantManagementDto>(`/tenants/${id}`, tenantData)

  return response.data
}

/**
 * Suspend a tenant organization
 * Terminates all active sessions and prevents login access
 */
export const suspendTenant = async (id: string): Promise<TenantManagementDto> => {
  const response = await api.post<TenantManagementDto>(`/tenants/${id}/suspend`)

  return response.data
}

/**
 * Activate a suspended tenant organization
 * Restores full access and functionality
 */
export const activateTenant = async (id: string): Promise<TenantManagementDto> => {
  const response = await api.post<TenantManagementDto>(`/tenants/${id}/activate`)

  return response.data
}

/**
 * Get authenticated tenant admin's own tenant details
 * Retrieves comprehensive tenant data including organization details, reseller relationship, and billing information
 */
export const getMyTenantDetails = async (): Promise<TenantManagementDto> => {
  const response = await api.get<TenantManagementDto>('/tenants/details/me')

  return response.data
}

/**
 * Permanently delete a QSE Retail tenant in incomplete or pending confirmation status
 * INTERNAL API - Performs complete cleanup of all tenant resources to allow re-registration
 * This operation is transactional and irreversible
 */
export const permanentDeleteTenant = async ({
  id,
  skipRetention,
}: PermanentDeleteTenantParams): Promise<PermanentDeleteTenantResponseDto> => {
  const response = await api.delete(`/tenants/${id}/permanent`, {
    params:
      typeof skipRetention === 'boolean'
        ? {
            skipRetention,
          }
        : undefined,
  })

  return response.data
}
